import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import { getAuth, getUser } from '../utils/auth';
import type { PermissionResource, PermissionAction, Location } from '../types';

// Admin roles that bypass permission checks (legacy fallback)
const ADMIN_ROLES = ['super_admin', 'org_owner', 'org_admin'];

/**
 * Check if user is an admin that bypasses permission checks.
 * Uses new flags (isSuperAdmin, isAdmin) with legacy role fallback.
 */
const isUserAdmin = (user: { isAdmin?: boolean; isSuperAdmin?: boolean; roleSystemKeys?: string[]; roles?: Array<{ systemKey?: string }> } | null): boolean => {
  if (!user) return false;

  // New approach: check flags from JWT (preferred)
  if (user.isSuperAdmin || user.isAdmin) return true;

  // Check roleSystemKeys array
  if (user.roleSystemKeys?.some(key => ADMIN_ROLES.includes(key))) return true;

  // Check roles array
  if (user.roles?.some(role => ADMIN_ROLES.includes(role.systemKey || ''))) return true;

  return false;
};

interface PermissionsContextType {
  permissions: string[];
  locationIds: string[];
  locations: Location[];
  hierarchyLevel: number; // 0 = admin, 1 = owner, 2 = manager, 3 = staff
  isLoading: boolean;
  isAdmin: boolean;
  hasPermission: (resource: PermissionResource, action: PermissionAction) => boolean;
  hasAnyPermission: (resource: PermissionResource) => boolean;
  canAccessLocation: (locationId: string) => boolean;
  canModifyRole: (roleHierarchyLevel: number) => boolean;
  refreshPermissions: () => void;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

interface PermissionsProviderProps {
  children: ReactNode;
}

export const PermissionsProvider = ({ children }: PermissionsProviderProps) => {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [hierarchyLevel, setHierarchyLevel] = useState<number>(3); // Default to lowest privilege

  // Get user data once and memoize based on user ID to prevent infinite loops
  // getUser() returns a new object on every call due to JSON.parse
  const rawUser = getUser();
  const rawAuth = getAuth();

  // Use refs to track the actual user ID and only update when it changes
  const userIdRef = useRef<string | undefined>(rawUser?.id);
  const userRef = useRef(rawUser);
  const authRef = useRef(rawAuth);

  // Only update refs when user ID actually changes
  if (rawUser?.id !== userIdRef.current) {
    userIdRef.current = rawUser?.id;
    userRef.current = rawUser;
    authRef.current = rawAuth;
  }

  const user = userRef.current;
  const auth = authRef.current;
  const isAdmin = isUserAdmin(user);

  // Fetch user's permissions from the backend
  const { data: permissionsData, isLoading: isLoadingPermissions, refetch: refetchPermissions } = useQuery({
    queryKey: ['userPermissions', user?.id],
    queryFn: async () => {
      const response = await axiosInstance.get('/permissions/me');
      return response.data;
    },
    enabled: !!user && !!auth?.access_token,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch user's locations
  const { data: userLocationsData, isLoading: isLoadingLocations, refetch: refetchLocations } = useQuery({
    queryKey: ['userLocations', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const response = await axiosInstance.get(`/permissions/users/${user.id}/locations`);
      return response.data;
    },
    enabled: !!user?.id && !!auth?.access_token,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch all locations for admin users or filter by user's locations
  const { data: locationsData } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const response = await axiosInstance.get('/locations');
      return response.data.data || response.data;
    },
    enabled: !!auth?.access_token,
    staleTime: 5 * 60 * 1000,
  });

  // Stable references for user properties to use in useEffect
  const userPermissions = user?.permissions;
  const userLocationIds = user?.locationIds;
  const userLocationId = user?.locationId;

  // Update permissions when data changes
  useEffect(() => {
    if (permissionsData?.permissions) {
      setPermissions(permissionsData.permissions);
    } else if (userPermissions) {
      // Fallback to permissions from user object
      setPermissions(userPermissions);
    }

    // Update hierarchy level from backend response
    if (permissionsData?.hierarchyLevel !== undefined) {
      setHierarchyLevel(permissionsData.hierarchyLevel);
    } else if (isAdmin) {
      setHierarchyLevel(0); // Admin has highest privilege
    }
  }, [permissionsData, userPermissions, isAdmin]);

  // Update locationIds when data changes
  useEffect(() => {
    if (userLocationsData) {
      const ids = userLocationsData.map((ul: any) => ul.locationId);
      setLocationIds(ids);
    } else if (userLocationIds) {
      setLocationIds(userLocationIds);
    } else if (userLocationId) {
      // Fallback to single locationId
      setLocationIds([userLocationId]);
    }
  }, [userLocationsData, userLocationIds, userLocationId]);

  // Check if user has a specific permission
  const hasPermission = useCallback(
    (resource: PermissionResource, action: PermissionAction): boolean => {
      // Admin bypass - always has all permissions
      if (isAdmin) return true;

      // Check if permission exists in the array
      const permissionKey = `${resource}:${action}`;
      return permissions.includes(permissionKey);
    },
    [permissions, isAdmin]
  );

  // Check if user has any permission for a resource (read, create, update, or delete)
  const hasAnyPermission = useCallback(
    (resource: PermissionResource): boolean => {
      if (isAdmin) return true;

      const actions: PermissionAction[] = ['read', 'create', 'update', 'delete'];
      return actions.some((action) => permissions.includes(`${resource}:${action}`));
    },
    [permissions, isAdmin]
  );

  // Check if user can access a specific location
  const canAccessLocation = useCallback(
    (locationId: string): boolean => {
      // Admin can access all locations
      if (isAdmin) return true;

      // If no locationIds are set, allow access (fallback behavior)
      if (!locationIds.length) return true;

      return locationIds.includes(locationId);
    },
    [locationIds, isAdmin]
  );

  // Check if user can modify a role based on hierarchy level
  const canModifyRole = useCallback(
    (roleHierarchyLevel: number): boolean => {
      // Admin can modify all roles
      if (isAdmin) return true;

      // User can only modify roles with HIGHER hierarchy level (lower privilege)
      return hierarchyLevel < roleHierarchyLevel;
    },
    [hierarchyLevel, isAdmin]
  );

  // Refresh permissions data
  const refreshPermissions = useCallback(() => {
    refetchPermissions();
    refetchLocations();
  }, [refetchPermissions, refetchLocations]);

  // Filter locations based on user's access
  const accessibleLocations = useMemo(
    () =>
      locationsData
        ? isAdmin
          ? locationsData
          : locationsData.filter((loc: Location) => locationIds.includes(loc.id))
        : [],
    [locationsData, isAdmin, locationIds]
  );

  const value: PermissionsContextType = useMemo(
    () => ({
      permissions,
      locationIds,
      locations: accessibleLocations,
      hierarchyLevel,
      isLoading: isLoadingPermissions || isLoadingLocations,
      isAdmin,
      hasPermission,
      hasAnyPermission,
      canAccessLocation,
      canModifyRole,
      refreshPermissions,
    }),
    [
      permissions,
      locationIds,
      accessibleLocations,
      hierarchyLevel,
      isLoadingPermissions,
      isLoadingLocations,
      isAdmin,
      hasPermission,
      hasAnyPermission,
      canAccessLocation,
      canModifyRole,
      refreshPermissions,
    ]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissionsContext = () => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissionsContext must be used within a PermissionsProvider');
  }
  return context;
};
